"use client";

import { useState } from "react";

import { getPalette } from "@/lib/palette";
import { usePrefersDark } from "@/lib/usePrefersDark";

export type SlopeSeries<M = unknown> = {
  key: string;
  label: string;
  color: string;
  /** Endpoint values along the y-axis, in y-axis units. */
  start: number;
  end: number;
  /** Optional richer end-label override (defaults to "{label} {endValueFormatter(end)}"). */
  endLabelOverride?: string;
  meta?: M;
};

type Props<M = unknown> = {
  series: SlopeSeries<M>[];
  height: number;
  startLabel: string;
  endLabel: string;
  yDomain?: [number, number];
  yTickFormatter?: (v: number) => string;
  endValueFormatter: (v: number) => string;
  /** Optional annotation drawn between two series at the right edge (e.g. a per-capita gap call-out). */
  rightAnnotation?: {
    fromSeriesKey: string;
    toSeriesKey: string;
    label: string;
    sublabel?: string;
    color: string;
  };
};

const VIEW_W = 1000;
const PAD_TOP = 24;
const PAD_BOTTOM = 36;
const PAD_LEFT = 60;
const PAD_RIGHT = 240; // room for end-of-line labels

export function SlopeChart<M = unknown>({
  series,
  height,
  startLabel,
  endLabel,
  yDomain,
  yTickFormatter = (v) => `${v}`,
  endValueFormatter,
  rightAnnotation,
}: Props<M>) {
  const p = getPalette(usePrefersDark());
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  const allValues = series.flatMap((s) => [s.start, s.end]);
  const computedMin = Math.min(...allValues, 0);
  const computedMax = Math.max(...allValues, 0);
  const [yMin, yMax] = yDomain ?? [
    Math.floor((computedMin - 5) / 10) * 10,
    Math.ceil((computedMax + 5) / 10) * 10,
  ];

  const VIEW_H = height;
  const plotTop = PAD_TOP;
  const plotBottom = VIEW_H - PAD_BOTTOM;
  const plotLeft = PAD_LEFT;
  const plotRight = VIEW_W - PAD_RIGHT;
  const plotH = plotBottom - plotTop;

  const yToPx = (y: number) =>
    plotBottom - ((y - yMin) / (yMax - yMin)) * plotH;

  // Build gridlines at every 10 units, plus the 0 line.
  const gridStep = niceStep(yMax - yMin);
  const gridStart = Math.ceil(yMin / gridStep) * gridStep;
  const gridLines: number[] = [];
  for (let v = gridStart; v <= yMax; v += gridStep) gridLines.push(v);
  if (!gridLines.includes(0) && yMin <= 0 && 0 <= yMax) gridLines.push(0);

  // Place end-of-line labels with simple anti-overlap nudging.
  const sortedByEnd = [...series].sort((a, b) => b.end - a.end);
  const labelGap = 18;
  const labels: { key: string; y: number }[] = [];
  let lastY = -Infinity;
  for (const s of sortedByEnd) {
    let y = yToPx(s.end);
    if (y < lastY + labelGap) y = lastY + labelGap;
    labels.push({ key: s.key, y });
    lastY = y;
  }
  const labelByKey = new Map(labels.map((l) => [l.key, l.y]));

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height={height}
        role="img"
        onMouseLeave={() => setHoverKey(null)}
      >
        {/* Gridlines */}
        {gridLines.map((v) => (
          <g key={`grid-${v}`}>
            <line
              x1={plotLeft}
              x2={plotRight}
              y1={yToPx(v)}
              y2={yToPx(v)}
              stroke={v === 0 ? p.ink : "var(--rule)"}
              strokeWidth={v === 0 ? 1 : 0.75}
              strokeDasharray={v === 0 ? undefined : "3 3"}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={plotLeft - 8}
              y={yToPx(v)}
              fontSize={11}
              textAnchor="end"
              dominantBaseline="middle"
              fill={p.ink}
              opacity={0.7}
            >
              {yTickFormatter(v)}
            </text>
          </g>
        ))}

        {/* Period axis labels */}
        <text
          x={plotLeft}
          y={plotBottom + 22}
          fontSize={12}
          fontWeight={500}
          textAnchor="middle"
          fill={p.ink}
        >
          {startLabel}
        </text>
        <text
          x={plotRight}
          y={plotBottom + 22}
          fontSize={12}
          fontWeight={500}
          textAnchor="middle"
          fill={p.ink}
        >
          {endLabel}
        </text>

        {/* Anchor ticks at the two columns */}
        <line
          x1={plotLeft}
          x2={plotLeft}
          y1={plotTop}
          y2={plotBottom}
          stroke={p.ink}
          strokeWidth={0.75}
          opacity={0.5}
          vectorEffect="non-scaling-stroke"
        />
        <line
          x1={plotRight}
          x2={plotRight}
          y1={plotTop}
          y2={plotBottom}
          stroke={p.ink}
          strokeWidth={0.75}
          opacity={0.5}
          vectorEffect="non-scaling-stroke"
        />

        {/* Series lines + endpoints */}
        {series.map((s) => {
          const y0 = yToPx(s.start);
          const y1 = yToPx(s.end);
          const dim = hoverKey != null && hoverKey !== s.key;
          return (
            <g
              key={s.key}
              opacity={dim ? 0.25 : 1}
              onMouseEnter={() => setHoverKey(s.key)}
            >
              <line
                x1={plotLeft}
                x2={plotRight}
                y1={y0}
                y2={y1}
                stroke={s.color}
                strokeWidth={2.5}
                vectorEffect="non-scaling-stroke"
              />
              <circle cx={plotLeft} cy={y0} r={4} fill={s.color} />
              <circle cx={plotRight} cy={y1} r={4} fill={s.color} />
            </g>
          );
        })}

        {/* Right annotation: gap bracket between two series. Drawn under labels. */}
        {rightAnnotation && (() => {
          const a = series.find((s) => s.key === rightAnnotation.fromSeriesKey);
          const b = series.find((s) => s.key === rightAnnotation.toSeriesKey);
          if (!a || !b) return null;
          const yA = yToPx(a.end);
          const yB = yToPx(b.end);
          const x = plotRight + 14;
          const yMidLine = (yA + yB) / 2;
          return (
            <g>
              <path
                d={`M ${plotRight + 6} ${yA} L ${x} ${yA} L ${x} ${yB} L ${plotRight + 6} ${yB}`}
                fill="none"
                stroke={rightAnnotation.color}
                strokeWidth={1.25}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={x + 6}
                y={yMidLine - 2}
                fontSize={12}
                fontWeight={600}
                fill={rightAnnotation.color}
                dominantBaseline="middle"
              >
                {rightAnnotation.label}
              </text>
              {rightAnnotation.sublabel && (
                <text
                  x={x + 6}
                  y={yMidLine + 14}
                  fontSize={11}
                  fill={rightAnnotation.color}
                  dominantBaseline="middle"
                  opacity={0.85}
                >
                  {rightAnnotation.sublabel}
                </text>
              )}
            </g>
          );
        })()}

        {/* End-of-line labels (placed to the right with leader segments to nudged positions) */}
        {series.map((s) => {
          const yEnd = yToPx(s.end);
          const yLabel = labelByKey.get(s.key) ?? yEnd;
          const labelX = plotRight + (rightAnnotation ? 90 : 12);
          return (
            <g
              key={`lbl-${s.key}`}
              onMouseEnter={() => setHoverKey(s.key)}
            >
              {Math.abs(yLabel - yEnd) > 2 && (
                <line
                  x1={plotRight + 4}
                  x2={labelX - 4}
                  y1={yEnd}
                  y2={yLabel}
                  stroke={s.color}
                  strokeWidth={0.75}
                  vectorEffect="non-scaling-stroke"
                  opacity={0.6}
                />
              )}
              <text
                x={labelX}
                y={yLabel}
                fontSize={12}
                fill={s.color}
                fontWeight={600}
                dominantBaseline="middle"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {s.endLabelOverride ?? endValueFormatter(s.end)}
              </text>
              <text
                x={labelX}
                y={yLabel + 14}
                fontSize={11}
                fill={p.ink}
                opacity={0.7}
                dominantBaseline="middle"
              >
                {s.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function niceStep(range: number): number {
  const targetTicks = 5;
  const raw = range / targetTicks;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  return nice * mag;
}
